export const PRIVATI_PAGES_EN = [
  {
    path: '/soluzioni/privati',
    kind: 'area',
    area: 'Individuals',
    areaPath: '/soluzioni/privati',
    eyebrow: 'Individuals area',
    title: 'Private insurance in Switzerland: home, family, mobility and assets.',
    summary: 'Personal liability, household contents, vehicles, property and income protection coordinated around the changes that matter in your life.',
    seoTitle: 'Private insurance in Switzerland | Insurance broker',
    seoDescription: 'Insurance review for individuals in Switzerland: personal liability, household contents, vehicles, home, travel and income protection. Find gaps and overlaps.',
    keywords: ['private insurance switzerland', 'personal liability switzerland', 'household contents insurance', 'insurance broker switzerland'],
    image: '/assets/soluzione-privati.png',
    updated: 'June 2026',
    readingTime: '5 min',
    intro: [
      'A person\u2019s insurance needs change with life. What a student leaving home needs is not what a family buying a property needs: that is why we organise solutions by situation, not by product.',
      'A review starts from existing policies and recent changes — moving, living together, children, a vehicle, buying a home or a new job — to identify where limits, insured people and priorities should be updated.',
    ],
    sections: [
      {
        id: 'protezione-quotidiana', type: 'cards', title: 'Everyday protection',
        cards: [
          { title: 'Personal liability', text: 'Covers damage you unintentionally cause to others. In Switzerland it is often required by landlords.' },
          { title: 'Household contents', text: 'Protects your belongings from theft, fire, water and natural events, at home and often also outside.' },
          { title: 'Legal protection', text: 'Support and cost cover in case of disputes: work, tenancy, traffic, contracts.' },
          { title: 'Vehicles', text: 'Liability, partial or comprehensive casco, roadside assistance: the right cover depends on the vehicle\u2019s value and use.' },
          { title: 'Travel', text: 'Cancellation, assistance and medical expenses abroad, to be coordinated with your health insurance.' },
          { title: 'Accidents and income', text: 'What happens if you cannot work: daily allowances and income protection.' },
        ],
      },
      {
        id: 'momenti-di-vita', type: 'paths', title: 'Guides for life\u2019s important moments',
        links: [
          { href: '/guide/privati/andare-a-vivere-da-soli', label: 'Living on your own', text: 'The first apartment: what you really need and what can wait.' },
          { href: '/guide/privati/trasferirsi-in-svizzera', label: 'Moving to Switzerland', text: 'Insurance, housing and formalities for the first months.' },
          { href: '/guide/privati/importare-auto-in-svizzera', label: 'Importing a car', text: 'Customs, registration and cover: the complete checklist.' },
          { href: '/guide/privati/comprare-casa-a-30-anni', label: 'Buying a home at 30', text: 'Financing, property cover and income protection.' },
          { href: '/guide/privati/mettersi-in-proprio', label: 'Going self-employed', text: 'From employee to self-employed: what is no longer automatic.' },
        ],
      },
      {
        id: 'quando-verificare', type: 'checklist', title: 'When a cover review is worthwhile',
        lead: 'A periodic review is especially useful after major changes.',
        items: [
          'Moving or changing home',
          'Marriage, cohabitation or separation',
          'Birth of a child',
          'Job change or switch to self-employment',
          'Purchase of a property or a vehicle',
          'Starting an independent activity',
        ],
      },
    ],
    faq: [
      { q: 'Is personal liability insurance mandatory?', a: 'Not by law in general, but it is required by almost all landlords and strongly recommended: damage to third parties can cost far more than years of premiums.' },
      { q: 'How often should policies be reviewed?', a: 'Roughly every 2–3 years, and in any case at every major change: move, children, new job, significant purchases.' },
      { q: 'Can I bring all my policies to a single contact person?', a: 'Yes, that is one of the advantages of working with a broker: a single interlocutor who knows your complete situation and coordinates your cover.' },
    ],
    cta: { title: 'Request a personal insurance check-up', text: 'We collect your policies, identify overlaps and gaps and propose the priorities.', label: 'Request a check-up' },
    related: ['/guide/privati/trasferirsi-in-svizzera', '/guide/privati/comprare-casa-a-30-anni'],
  },
  {
    path: '/guide/privati/andare-a-vivere-da-soli',
    kind: 'guide',
    area: 'Individuals',
    areaPath: '/soluzioni/privati',
    eyebrow: 'Practical guide · Individuals',
    title: 'Living on your own: insurance for your first home.',
    summary: 'For students and young adults: what you really need when you leave your parents\u2019 home, and what can wait.',
    updated: 'June 2026',
    readingTime: '6 min',
    intro: [
      'The first apartment is an important step, also from an insurance point of view: some cover that was previously included in your parents\u2019 policy now has to be activated by you.',
    ],
    sections: [
      {
        id: 'cosa-cambia', type: 'text', title: 'What changes when you leave home',
        paragraphs: ['With the move, your responsibility towards third parties, the landlord and your belongings changes.'],
        bullets: [
          'You become responsible for damage you cause to others',
          'The rented apartment must be returned without damage',
          'Your belongings are no longer covered by your parents\u2019 policy',
          'Your health insurance must be updated with the new domicile',
        ],
      },
      {
        id: 'coperture', type: 'cards', title: 'Cover to consider',
        cards: [
          { title: 'Personal liability', text: 'Priority number one: required by almost all landlords, it covers damage to third parties and often rental damage too.' },
          { title: 'Household contents', text: 'Protects your belongings. With few furnishings the insured value is low and the premium modest.' },
          { title: 'Legal protection', text: 'Useful for disputes with the landlord or at work. Can also be considered later.' },
          { title: 'Rental deposit', text: 'As an alternative to a bank deposit, guarantee solutions exist: compare costs and conditions.' },
        ],
      },
      {
        id: 'contratto-locazione', type: 'checklist', title: 'Before signing the tenancy agreement',
        items: [
          'Read the tenant\u2019s responsibilities in the contract',
          'Check the amount of the required deposit',
          'Document the condition of the property with dated photos',
          'Carefully complete the handover report',
          'Clarify who is responsible for any pre-existing damage',
        ],
      },
      {
        id: 'budget', type: 'text', title: 'A realistic monthly budget',
        paragraphs: [
          'Beyond the rent, budget for: health insurance, utilities, insurance, transport and a small reserve for the unexpected. Basic insurance for a first apartment (liability + household) generally has a modest annual cost: it is worth not giving it up.',
        ],
      },
    ],
    faq: [
      { q: 'Do I really need household insurance with few belongings?', a: 'Often yes: the policy also covers theft away from home and water or fire damage. With low values the premium is modest. In some cases you can temporarily remain on the family policy: this should be checked with the insurer.' },
      { q: 'Who pays if I break something in the rented apartment?', a: 'Rental damage is usually covered by personal liability insurance, if included. Check this in the conditions before signing the policy.' },
      { q: 'Do I have to inform my health insurer of the move?', a: 'Yes: the premium also depends on your place of domicile. Communicate the change of address to the insurer and update your details.' },
    ],
    cta: { title: 'Prepare your first insurance package', text: 'Two essential covers, chosen well: we help you start on the right foot, without overspending.', label: 'Prepare your package' },
    related: ['/guide/privati/trasferirsi-in-svizzera', '/guide/privati/mettersi-in-proprio'],
  },
  {
    path: '/guide/privati/trasferirsi-in-svizzera',
    kind: 'guide',
    area: 'Individuals',
    areaPath: '/soluzioni/privati',
    eyebrow: 'Practical guide · Individuals',
    title: 'Moving to Switzerland: what to consider.',
    summary: 'Insurance, housing, work and formalities: the entry guide for those arriving in Switzerland.',
    updated: 'June 2026',
    readingTime: '8 min',
    intro: [
      'Those moving to Switzerland encounter an insurance system that differs from many other countries: health insurance is mandatory, liability cover is required by landlords, pension provision works in pillars. This guide helps you put things in order.',
    ],
    sections: [
      {
        id: 'prime-priorita', type: 'checklist', title: 'The first priorities',
        items: [
          'Registration of domicile and residence permit',
          'Health insurance: mandatory within the prescribed deadlines',
          'Tenancy agreement and deposit',
          'Personal liability and household contents',
          'Accident cover: through the employer or health insurance',
          'Vehicle, driving licence and any import formalities',
        ],
      },
      {
        id: 'percorsi', type: 'cards', title: 'Employee, cross-border worker or self-employed',
        lead: 'Needs vary greatly depending on your work status.',
        cards: [
          { title: 'Employee', text: 'Accident and occupational pension go through the employer. You still need to organise health insurance, liability and household cover.' },
          { title: 'Cross-border worker', text: 'Special rules for healthcare, taxation and pension: a dedicated review avoids double cover or gaps.' },
          { title: 'Self-employed', text: 'Accidents, daily allowances and pension are your responsibility: also read the guide on going self-employed.' },
        ],
      },
      {
        id: 'primi-giorni', type: 'timeline', title: 'The first days: a possible sequence',
        steps: [
          { label: 'Week 1', text: 'Registration at the residents\u2019 office, permit, opening a bank account.' },
          { label: 'Weeks 2–4', text: 'Choice of health insurer and submission of documents. Activation of liability and household cover.' },
          { label: 'First months', text: 'Check accident cover, vehicle and licence, pension planning and coordination with foreign policies.' },
        ],
      },
      {
        id: 'errori', type: 'errors', title: 'Common mistakes of newcomers',
        items: [
          'Postponing the choice of health insurer beyond the deadlines',
          'Keeping foreign cover without checking its validity in Switzerland',
          'Not coordinating accidents and illness, with double cover or gaps',
          'Signing the tenancy agreement without the required liability cover',
          'Underestimating pension and tax matters in the new country',
        ],
      },
      {
        id: 'attenzione-cassa', type: 'warning', title: 'Health insurance doesn\u2019t wait',
        text: 'Health insurance is mandatory for those settling in Switzerland and must be activated within the deadlines from arrival, with retroactive effect. The sooner you act, the simpler the administration.',
      },
    ],
    faq: [
      { q: 'By when must I choose a health insurer?', a: 'As a rule within three months of arrival, with cover retroactive to the entry date. Premiums are due from the start date, so the sooner you choose, the better.' },
      { q: 'Are my foreign insurance policies still valid?', a: 'It depends on the cover: many policies have limited territorial validity. Have them checked before relying on them in Switzerland.' },
      { q: 'Is liability cover needed to rent?', a: 'It is not mandatory by law, but almost all landlords require it as a condition of the contract.' },
      { q: 'What changes if I am a cross-border worker?', a: 'Healthcare, taxation and pension follow specific rules and bilateral agreements. It deserves dedicated advice before deciding.' },
    ],
    cta: { title: 'Organise your insurance move', text: 'One meeting to put health insurance, housing and basic cover in order: without mistakes and without overlaps.', label: 'Organise your move' },
    related: ['/guide/privati/andare-a-vivere-da-soli', '/guide/privati/importare-auto-in-svizzera'],
  },
  {
    path: '/guide/privati/importare-auto-in-svizzera',
    kind: 'guide',
    area: 'Individuals',
    areaPath: '/soluzioni/privati',
    eyebrow: 'Practical guide · Individuals',
    title: 'Importing a car into Switzerland: customs, registration and insurance.',
    summary: 'The orderly checklist for those bringing a vehicle into Switzerland, from documents to plates.',
    updated: 'June 2026',
    readingTime: '7 min',
    intro: [
      'Importing a vehicle into Switzerland requires a few precise administrative steps. Insurance comes into play twice: during the transfer and at registration.',
    ],
    sections: [
      {
        id: 'prima', type: 'checklist', title: 'Before importing',
        items: [
          'Ownership documents and vehicle registration certificate',
          'Check of technical conformity for Switzerland',
          'Estimate of import taxes and duties',
          'Organising transport and cover during the transfer',
          'Any temporary plates for the journey',
        ],
      },
      {
        id: 'dopo', type: 'timeline', title: 'After arrival: the sequence',
        steps: [
          { label: 'Step 1', title: 'Customs declaration', text: 'The vehicle must be declared at customs with ownership documents and any duties.' },
          { label: 'Step 2', title: 'Technical inspection', text: 'Inspection at the road traffic office to verify the vehicle\u2019s conformity.' },
          { label: 'Step 3', title: 'Insurance certificate', text: 'The insurer transmits the electronic insurance certificate: without it, no plates.' },
          { label: 'Step 4', title: 'Registration', text: 'With the inspection and certificate you can request Swiss plates and the registration document.' },
        ],
      },
      {
        id: 'copertura', type: 'cards', title: 'How to choose the cover',
        cards: [
          { title: 'Liability', text: 'Mandatory: covers damage to third parties. The insurance certificate is the prerequisite for plates.' },
          { title: 'Partial casco', text: 'Theft, fire, natural events, glass breakage, collision with animals.' },
          { title: 'Comprehensive casco', text: 'Adds own collision damage. Sensible for valuable or recent vehicles.' },
          { title: 'Useful extensions', text: 'Roadside assistance, gross negligence protection, enhanced market value, replacement vehicle.' },
        ],
      },
      {
        id: 'costi', type: 'text', title: 'The overall cost to budget for',
        bullets: [
          'Purchase price and vehicle transport',
          'Import taxes and duties',
          'Technical inspection and any adjustments',
          'Registration and plates',
          'Annual insurance premium',
          'Initial maintenance',
        ],
      },
    ],
    faq: [
      { q: 'Can I drive in Switzerland with foreign plates?', a: 'For limited periods and under specific conditions. Those settling in Switzerland must register the vehicle within the prescribed deadlines.' },
      { q: 'What exactly is the insurance certificate?', a: 'It is the electronic confirmation of liability cover that the insurer sends to the road traffic office. Without the certificate the vehicle cannot be registered.' },
      { q: 'Is comprehensive casco worth it on an imported car?', a: 'It depends on the vehicle\u2019s value and your risk profile. On recent or valuable vehicles it is often advisable; on older vehicles partial casco is worth considering.' },
    ],
    cta: { title: 'Request a check before registration', text: 'We check documents, timing and the right cover for your vehicle together.', label: 'Request a check' },
    related: ['/guide/privati/trasferirsi-in-svizzera', '/guide/privati/andare-a-vivere-da-soli'],
  },
  {
    path: '/guide/privati/comprare-casa-a-30-anni',
    kind: 'guide',
    area: 'Individuals',
    areaPath: '/soluzioni/privati',
    eyebrow: 'Practical guide · Individuals',
    title: 'Buying a home at 30: financing, cover and income protection.',
    summary: 'The first owned home is also an insurance decision: here is what to assess before signing.',
    updated: 'June 2026',
    readingTime: '8 min',
    intro: [
      'Buying your first home means taking on a long-term financial commitment. The right insurance protects not only the walls: it protects your ability to pay the mortgage even if something goes wrong.',
    ],
    sections: [
      {
        id: 'prima-acquisto', type: 'checklist', title: 'Before buying',
        items: [
          'Available equity and its composition',
          'Affordability of the financing relative to income',
          'Ancillary costs: notary, land registry, any renovations',
          'Ordinary and extraordinary maintenance over time',
          'Taxes and recurring property costs',
          'Liquidity reserve for the unexpected',
        ],
      },
      {
        id: 'coperture', type: 'cards', title: 'Cover to consider',
        cards: [
          { title: 'Building insurance', text: 'Fire, natural events, water: protects the building. Many cantons have specific rules.' },
          { title: 'Owner\u2019s liability', text: 'Covers damage the property can cause to third parties, for example due to maintenance defects.' },
          { title: 'Real estate legal protection', text: 'Disputes with contractors, neighbours or authorities: legal costs covered.' },
          { title: 'Death and disability risk', text: 'Protects the mortgage if income is lost: fundamental for those buying with financing.' },
        ],
      },
      {
        id: 'proteggere-finanziamento', type: 'text', title: 'Protecting the financing',
        paragraphs: [
          'A mortgage is a commitment lasting decades. What happens in case of death, inability to work or separation? Without adequate protection, the risk falls on the family or the partner.',
          'Typical solutions combine term life insurance, loss-of-earnings cover and coordination with pension provision. The third pillar can also be involved in the purchase: it must be managed carefully.',
        ],
      },
      {
        id: 'caso', type: 'case', title: 'A typical case',
        caseStudy: {
          situation: 'A couple aged 31, two employee incomes, first owned apartment with an 80% mortgage.',
          problem: 'The bank approves the financing, but no one has checked what happens to the mortgage if one of them can no longer work.',
          risks: ['Reduced income in case of prolonged illness', 'Mortgage unsustainable on a single salary', 'Property cover not coordinated with the purchase contract'],
          decisions: ['Affordability analysis on a single income', 'Loss-of-earnings cover for both', 'Term life insurance linked to the mortgage debt', 'Coordination between building, owner\u2019s liability and legal protection'],
          result: 'Financing maintained, with a protection plan proportionate to the debt and real incomes.',
        },
      },
    ],
    faq: [
      { q: 'Can I use the second or third pillar to buy a home?', a: 'Under certain conditions yes, with effects on pension and taxation to be assessed carefully. It is a decision to coordinate, not to improvise.' },
      { q: 'Is building insurance mandatory?', a: 'It depends on the canton: some have mandatory cantonal building insurance, in others cover is private. The bank in any case requires the property to be insured.' },
      { q: 'Is term life cover really necessary?', a: 'For those buying with a mortgage and a partner or family, it is often the most important protection: it ensures the debt does not fall on those who remain.' },
    ],
    cta: { title: 'Assess the protection of your first home', text: 'Before signing, we check financing, property cover and income protection together.', label: 'Assess the protection' },
    related: ['/soluzioni/previdenza', '/guide/privati/mettersi-in-proprio'],
  },
  {
    path: '/guide/privati/mettersi-in-proprio',
    kind: 'guide',
    area: 'Individuals',
    areaPath: '/soluzioni/privati',
    eyebrow: 'Practical guide · Individuals',
    title: 'Going self-employed: from employee to independent without gaps.',
    summary: 'When you leave your permanent job, some protection is no longer automatic. Here is what to organise before you start.',
    updated: 'June 2026',
    readingTime: '8 min',
    intro: [
      'As an employee, accidents, daily allowances and occupational pension go through the employer. As a self-employed person, all of this becomes your responsibility: the transition is when the most dangerous gaps open up.',
    ],
    sections: [
      {
        id: 'cosa-cambia', type: 'cards', title: 'What changes compared to employment',
        cards: [
          { title: 'Accidents', text: 'You are no longer covered by the employer\u2019s UVG/LAA: cover must be activated by you, via health insurance or a dedicated policy.' },
          { title: 'Daily allowances', text: 'In case of illness nobody pays your salary: you need cover for loss of income.' },
          { title: 'Occupational pension', text: 'The second pillar is no longer automatic: pension provision must be reorganised from scratch.' },
          { title: 'Professional liability', text: 'Mistakes in your work become your risk: consider professional liability cover.' },
        ],
      },
      {
        id: 'checklist', type: 'checklist', title: 'The checklist before you start',
        items: [
          'Define the activity and the legal form',
          'Estimate turnover and fixed costs for the first year',
          'Check social security cover (AHV/AVS and pension)',
          'Protect income in case of illness or accident',
          'Separate private risks from professional ones',
          'Assess professional liability and legal protection',
        ],
      },
      {
        id: 'caso', type: 'case', title: 'A typical case',
        caseStudy: {
          situation: 'IT consultant, 34, leaves his permanent job to work independently with his first two clients secured.',
          problem: 'After resigning he discovers that accidents and daily allowances are no longer covered, and that his pension provision has stopped.',
          risks: ['Loss of income in case of illness beyond the first days', 'No professional accident cover', 'Pension gap accumulating year after year', 'Liability towards clients for consulting errors'],
          decisions: ['Daily allowance with waiting period calibrated to savings', 'Accident cover via dedicated insurance', 'Pension plan with third pillar and a BVG/LPP solution for the self-employed', 'Professional liability for consulting risks'],
          result: 'Transition completed without gaps, with insurance costs planned into the first-year budget.',
        },
      },
      {
        id: 'forma-giuridica', type: 'warning', title: 'Sole trader or company?',
        text: 'The choice between sole proprietorship, Sagl/GmbH or SA/AG changes liability, pension and the cover you need. We explore the comparison in the guide to legal forms.',
      },
    ],
    faq: [
      { q: 'From when must I activate cover as self-employed?', a: 'Ideally from the first day of activity. Some cover, such as daily allowances, has waiting periods: organising it early avoids gaps.' },
      { q: 'How much does protection as a self-employed person cost?', a: 'It depends on income, activity and chosen cover. At the start it pays to set priorities: first the risks that compromise income and continuity, then the extensions.' },
      { q: 'Is pension provision mandatory for the self-employed?', a: 'AHV/AVS is mandatory; the second pillar is not, but giving it up creates significant gaps. There are specific solutions for the self-employed worth assessing.' },
    ],
    cta: { title: 'Analyse the move to self-employment', text: 'Before resigning, let\u2019s build your protection plan together: income, accidents, pension and liability.', label: 'Analyse the transition' },
    related: ['/guide/aziende/forme-giuridiche-svizzera', '/guide/aziende/aprire-un-azienda'],
  },
];
